import Divider from '@/components/common/Divider';
import { findInstrumentInfo } from '@/components/common/InstrumentInfo';
import {
    BidAsks,
    MarketInstrument,
    MarketListHeader,
    UserMarketProps
} from '@/components/modules/MarketList/UserMarket';
import { switchOrderBook } from '@/services/MarketService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { selectActiveMarketPair } from '@/state/reducers/marketPairSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import {
    addSubscription,
    removeSubscription,
    selectCurrentWatchList,
    selectTraderWatchLists,
    setShouldReinstateWatchlist
} from '@/state/reducers/traderMarketSlice';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import { useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { forwardRef, Fragment, useMemo } from 'react';
import { MdAddCircleOutline, MdOutlineRemoveRedEye, MdRemoveCircleOutline } from 'react-icons/md';

const TraderUserMarket = forwardRef<HTMLDivElement, UserMarketProps>((props: UserMarketProps, ref) => {
    const { market, style, parentSize = 0, isActive, onClick, onActive } = props;

    const dispatch = useAppDispatch();

    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);
    const activePair = useAppSelector(selectActiveMarketPair);
    const watchLists = useAppSelector(selectTraderWatchLists);
    const selectedWatchList = useAppSelector(selectCurrentWatchList);
    const watchList = useMemo(() => selectedWatchList || watchLists[0], [watchLists, selectedWatchList]);

    const isXplorSpot = !location.pathname.includes('/trader');

    const { instrumentsIncludeRatioApi } = useSso();
    const { data: instruments } = instrumentsIncludeRatioApi;

    const publicInstrumentConfig = useMemo(() => {
        return getInstrumentConfig(accountType, instrumentConfigs, market?.celer);
    }, [market, instrumentConfigs, accountType]);

    const detailedInstrumentConfig = useMemo(() => {
        return instruments?.find((instrument) => {
            return findInstrumentInfo(isXplorSpot, publicInstrumentConfig, instrument, market);
        });
    }, [publicInstrumentConfig, instruments, market]);

    const generalInfo = useMemo(() => {
        return {
            description: detailedInstrumentConfig?.description || '',
            cfi: detailedInstrumentConfig?.cfi || '',
            upi: detailedInstrumentConfig?.upi || ''
        };
    }, [detailedInstrumentConfig]);

    const tradeInfo = useMemo(() => {
        return {
            type: publicInstrumentConfig.type
        };
    }, [publicInstrumentConfig]);

    const isActivePair = useMemo(() => {
        return activePair?.celer === market.celer;
    }, [activePair, market]);

    const isInWatchlist = useMemo(() => {
        return watchList.markets.some((m) => m.celer === market.celer);
    }, [watchList]);

    return (
        <Fragment>
            {market.celer === 'header' ? (
                <MarketListHeader
                    ref={ref}
                    style={style}
                    parentSize={parentSize}
                    generalInfo={generalInfo}
                    tradeInfo={tradeInfo}
                />
            ) : (
                <div
                    ref={ref}
                    style={style}
                    className={cn('hover:bg-brand-neutral-700', {
                        'bg-brand-background-dark': isActive
                    })}>
                    <div
                        className={cn('cursor-pointer flex text-xs py-1.5 px-2 text-brand-text justify-between')}
                        onClick={onActive}>
                        <MarketInstrument {...props} />
                        <BidAsks {...props} generalInfo={generalInfo} tradeInfo={tradeInfo} />
                    </div>
                    {isActive && <Divider className="bg-brand-neutral-700" />}
                    {isActive && (
                        <div className="flex text-brand-text text-xs h-[28px] border-b border-brand-neutral-700 gap-0.5 bg-brand-background-dark">
                            <div
                                className={cn('flex justify-center items-center gap-1 whitespace-nowrap px-2', {
                                    'cursor-pointer bg-brand-primary text-brand-text hover:bg-brand-primary-light':
                                        !isActivePair,
                                    'cursor-not-allowed bg-brand-primary text-brand-text': isActivePair
                                })}
                                onClick={() => {
                                    switchOrderBook(market, 'trader');
                                    onClick && onClick();
                                }}>
                                <MdOutlineRemoveRedEye className="w-3.5 h-3.5" />
                                <span className="">View Chart</span>
                            </div>
                            {!isInWatchlist ? (
                                <div
                                    className="flex justify-center items-center gap-1 cursor-pointer whitespace-nowrap text-brand-text bg-brand-primary hover:bg-brand-primary-light px-2"
                                    onClick={() => {
                                        dispatch(addSubscription({ id: watchList.id, market }));
                                        dispatch(setShouldReinstateWatchlist({ mode: 'add', market }));
                                    }}>
                                    <MdAddCircleOutline className="w-3.5 h-3.5" />
                                    <span className="">Add Ticker</span>
                                </div>
                            ) : (
                                <div
                                    className="flex justify-center items-center gap-1 cursor-pointer whitespace-nowrap bg-brand-red hover:bg-brand-red-light text-brand-text px-2"
                                    onClick={() => {
                                        dispatch(removeSubscription({ id: watchList.id, market }));
                                        dispatch(setShouldReinstateWatchlist({ mode: 'remove', market }));
                                    }}>
                                    <MdRemoveCircleOutline className="w-3.5 h-3.5" />
                                    <span className="">Remove Ticker</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
});

TraderUserMarket.displayName = 'TraderUserMarket';

export default TraderUserMarket;
